import { useEffect, useState } from 'react';
import { portalAPI } from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Bell, Megaphone, ShieldAlert, Newspaper, Wrench, Loader2, Smartphone } from 'lucide-react';
import { getPushStatus, subscribeToPush, unsubscribeFromPush, isPushSupported } from '../utils/pushNotifications';

const CATEGORIES = [
  {
    key: 'system_updates',
    label: 'System & Product Updates',
    description: 'New features, firmware, and important product announcements.',
    icon: Wrench,
  },
  {
    key: 'warranty_updates',
    label: 'Warranty Updates',
    description: 'Registration approvals, status changes, and expiry reminders.',
    icon: Bell,
  },
  {
    key: 'newsletters',
    label: 'Newsletters',
    description: 'Tips, recipes, and news from Chinook Manufacturing.',
    icon: Newspaper,
  },
];

/**
 * CustomerPortalNotificationsPage - email notification preferences.
 * Recalls / safety notices are mandatory and cannot be turned off.
 */
const CustomerPortalNotificationsPage = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushStatus, setPushStatus] = useState({ supported: false, subscribed: false, permission: 'default' });
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    getPushStatus().then(setPushStatus);
  }, []);

  const handlePushToggle = async (value) => {
    setPushBusy(true);
    try {
      if (value) {
        await subscribeToPush();
        toast.success('Push notifications enabled on this device');
      } else {
        await unsubscribeFromPush();
        toast.success('Push notifications disabled on this device');
      }
      setPushStatus(await getPushStatus());
    } catch (e) {
      toast.error(e.message || 'Could not update push notifications');
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await portalAPI.getNotificationPreferences();
        setPrefs(res.data);
      } catch (error) {
        toast.error('Failed to load notification preferences');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (key, value) => {
    const previous = prefs;
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setSaving(true);
    try {
      const res = await portalAPI.updateNotificationPreferences({ [key]: value });
      setPrefs(res.data);
      toast.success('Preferences updated');
    } catch (error) {
      setPrefs(previous);
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="customer-portal-notifications">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="h-6 w-6 text-blue-600" />
          Email Notifications
        </h1>
        <p className="text-muted-foreground mt-1">
          Choose which updates you&apos;d like to receive by email.
        </p>
      </div>

      {/* Push notifications on this device */}
      <Card data-testid="push-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-blue-600" /> Push Notifications
          </CardTitle>
          <CardDescription>Get instant alerts on this device — recalls, safety notices, and support replies.</CardDescription>
        </CardHeader>
        <CardContent>
          {!isPushSupported() ? (
            <p className="text-sm text-muted-foreground">
              This browser doesn&apos;t support push notifications. Install the app (Add to Home Screen) and open it to enable them.
            </p>
          ) : pushStatus.permission === 'denied' ? (
            <p className="text-sm text-amber-600">
              Notifications are blocked in your browser settings. Allow notifications for this site, then re-enable here.
            </p>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">Enable push on this device</p>
                <p className="text-sm text-muted-foreground">You&apos;ll get alerts even when the app isn&apos;t open.</p>
              </div>
              <div className="flex items-center gap-2">
                {pushBusy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <Switch
                  checked={pushStatus.subscribed}
                  disabled={pushBusy}
                  onCheckedChange={handlePushToggle}
                  data-testid="push-toggle"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Your Preferences</CardTitle>
          <CardDescription>You can change these at any time.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {CATEGORIES.map(({ key, label, description, icon: Icon }) => (
            <div
              key={key}
              className="flex items-center justify-between gap-4 py-3 border-b last:border-b-0"
              data-testid={`notif-row-${key}`}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Icon className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-medium">{label}</p>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              </div>
              <Switch
                checked={!!prefs?.[key]}
                disabled={saving}
                onCheckedChange={(val) => handleToggle(key, val)}
                data-testid={`notif-toggle-${key}`}
              />
            </div>
          ))}

          {/* Mandatory recalls / safety */}
          <div className="flex items-center justify-between gap-4 py-3 bg-red-50 -mx-6 px-6 mt-2 rounded-b-lg">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <ShieldAlert className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="font-medium text-red-900 flex items-center gap-2">
                  <span>Recalls &amp; Safety Notices</span>
                  <Badge className="bg-red-100 text-red-700">Always on</Badge>
                </div>
                <p className="text-sm text-red-700">
                  For your safety, recall and safety notices are always sent and cannot be turned off.
                </p>
              </div>
            </div>
            <Switch checked disabled data-testid="notif-toggle-recalls" />
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Megaphone className="h-3 w-3" />
        We respect your inbox — you&apos;ll only receive the emails you opt into (plus mandatory safety notices).
      </p>
    </div>
  );
};

export default CustomerPortalNotificationsPage;
